import { useState, useEffect, useCallback, useContext } from 'react';
import { getMaxWidth, ITEM_WIDTH } from '../utils/featureSelectorUtils';
import { useTrackingManager } from './useTracking';
import { ContentContext } from '../context/ContentContext';

const isScrollable = (
  containerRef: React.RefObject<HTMLDivElement>,
  itemCount: number,
): boolean => {
  if (!containerRef.current || typeof window === 'undefined') return false;
  const maxWidth = getMaxWidth(window.innerWidth);
  const totalItemWidth = ITEM_WIDTH * itemCount;
  return totalItemWidth > maxWidth; // Scroll is possible only if items exceed container width
};

const calculateScrollWidth = (
  containerRef: React.RefObject<HTMLDivElement>,
  isScrollForward: boolean,
) => {
  const container = containerRef.current;
  if (!container || typeof window === 'undefined') return 0;
  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  const currentScrollLeft = container.scrollLeft;
  const maxWidth = getMaxWidth(window.innerWidth);
  // Calculate the remaining scrollable width
  const remainingScrollWidth = isScrollForward
    ? maxScrollLeft - currentScrollLeft
    : currentScrollLeft;
  return Math.min(remainingScrollWidth, maxWidth - ITEM_WIDTH - 6); // ITEM_WIDTH = button overlap, 6 = padding
};

export const useScrollButtons = (
  containerRef: React.RefObject<HTMLDivElement>,
  itemCount: number,
  type: 'colors' | 'rims',
) => {
  const [showBackButton, setShowBackButton] = useState(false);
  const [showForwardButton, setShowForwardButton] = useState(false);
  const [scrollTrackedForColors, setScrollTrackedForColors] = useState(false);
  const [scrollTrackedForRims, setScrollTrackedForRims] = useState(false);
  const trackingManager = useTrackingManager();
  const { carlineId } = useContext(ContentContext);

  useEffect(() => {
    const forwardVisible = isScrollable(containerRef, itemCount);
    const backVisible = false; // always false in this effect

    setShowForwardButton(forwardVisible);
    setShowBackButton(backVisible);
  }, [containerRef, itemCount]);

  const handleScroll = useCallback(
    (isScrollForward: boolean) => {
      const container = containerRef.current;
      if (!container) return;
      const scrollWidth = calculateScrollWidth(containerRef, isScrollForward);
      const newScrollLeft = isScrollForward
        ? container.scrollLeft + scrollWidth
        : container.scrollLeft - scrollWidth;
      // TODO: if smoother animation or easing is necessary use requestAnimationFrame
      container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });

      setShowBackButton(newScrollLeft > 0);
      setShowForwardButton(newScrollLeft < container.scrollWidth - container.clientWidth);

      if (!scrollTrackedForColors && type === 'colors') {
        trackingManager.click({
          eventAction: 'content',
          eventName: 'scroll in colors',
          elementName: 'icon',
          value: carlineId,
        });
        setScrollTrackedForColors(true);
      } else if (!scrollTrackedForRims && type === 'rims') {
        trackingManager.click({
          eventAction: 'content',
          eventName: 'scroll in rims',
          elementName: 'icon',
          value: carlineId,
        });
        setScrollTrackedForRims(true);
      }
    },
    [containerRef, scrollTrackedForRims, scrollTrackedForColors],
  );

  return {
    showBackButton,
    showForwardButton,
    handleScroll,
  };
};
